import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import ListItemIcon from '@mui/material/ListItemIcon';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';

import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ReplyIcon from '@mui/icons-material/Reply';

import ToasterBox from '../../../../../components/singleComponents/toaster/ToasterBox';

import {
  matchStatus,
  matchFeedbackType,
  matchPriority,
} from '../../utils/feedback';

import {
  RootContainer,
  Container,
  StatusBox,
  HeadRowStyled,
  HeadCellStyled,
  BodyCellStyled,
  TableRowStyled,
  NoTicketsMsg,
} from './index.styles';

import api, { authHeader } from '../../../../../../services/API/apiConfig';

export function TableFeedbacks({ searchValue, filterOptions }) {
  const { id: userId } = JSON.parse(localStorage.getItem('user'));
  const navigate = useNavigate();

  const [feedbacks, setFeedbacks] = useState([]);
  const [filteredFeedbacks, setFilteredFeedbacks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuState, setMenuState] = useState({
    anchorEl: null,
    ticketId: null,
  });
  const [toaster, setToaster] = useState({ open: false, msg: '' });

  const handleRowMenu = (e, ticketId) => {
    e.stopPropagation();
    setMenuState({
      anchorEl: e.currentTarget,
      ticketId,
    });
  };

  const handleClose = (event) => {
    if (event) event.stopPropagation();
    setMenuState({
      anchorEl: null,
      ticketId: null,
    });
  };

  const replyTicket = (e, id) => {
    e.stopPropagation();
    navigate(`/ticket/${id}`);
  };

  const markAsRead = async (e, id) => {
    e.stopPropagation();

    const formData = new FormData();
    formData.append('read_by_admin', true);

    try {
      await api.patch(`/feedbacks/${id}`, formData, {
        headers: authHeader(true),
      });
      setToaster({
        open: true,
        msg: 'Feedback atualizado com sucesso.',
      });
    } catch (err) {
      if (err.response.data.messages === 'O Feedback já foi encerrado') {
        setToaster({
          open: true,
          msg: 'Esse feedback já foi encerrado e por isso não pode ser editado.',
        });
      } else {
        setToaster({
          open: true,
          msg: 'Não foi possível editar o feedback.',
        });
      }
    } finally {
      setTimeout(() => setToaster({ open: false, msg: '' }), 5000);
    }
  };

  const renderTableRows = (item) => {
    const status = matchStatus(item.status);

    let backgroundColor = '#D1D1D1';
    if (status === 'Aberto') backgroundColor = '#FF9F09';
    if (status === 'Respondido') backgroundColor = '#29CC6A';

    return (
      <TableRowStyled
        onClick={() => navigate(`/ticket/${item.id}`)}
        key={item.id}
      >
        <BodyCellStyled sx={{ width: '10%' }}>
          <StatusBox style={{ backgroundColor }}>{status}</StatusBox>
        </BodyCellStyled>
        <BodyCellStyled sx={{ width: '5%' }}>{item.id}</BodyCellStyled>
        <BodyCellStyled sx={{ width: '15%' }}>
          {matchFeedbackType(item.feedback_type)}
        </BodyCellStyled>
        <BodyCellStyled sx={{ width: '10%' }}>
          {matchPriority(item.priority)}
        </BodyCellStyled>
        <BodyCellStyled sx={{ width: '20%' }}>{item.subject}</BodyCellStyled>
        <BodyCellStyled
          sx={{
            width: '35%',
            textOverflow: 'ellipsis',
          }}
        >
          {item.description}
        </BodyCellStyled>
        {[1, 164].includes(userId) && (
          <BodyCellStyled sx={{ width: '5%' }}>
            <IconButton
              onClick={(e) => handleRowMenu(e, item.id)}
              size="small"
              aria-controls={
                menuState.ticketId === item.id
                  ? `row-menu-${item.id}`
                  : undefined
              }
              aria-haspopup="true"
              aria-expanded={
                menuState.ticketId === item.id ? 'true' : undefined
              }
            >
              <MoreVertIcon />
            </IconButton>
            <Menu
              id={`row-menu-${item.id}`}
              anchorEl={
                menuState.ticketId === item.id ? menuState.anchorEl : null
              }
              open={menuState.ticketId === item.id}
              onClose={handleClose}
              onClick={handleClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <MenuItem onClick={(e) => replyTicket(e, item.id)}>
                <ListItemIcon>
                  <ReplyIcon sx={{ color: '#5ea8eb' }} fontSize="small" />
                </ListItemIcon>
                Responder
              </MenuItem>
              <MenuItem onClick={(e) => markAsRead(e, item.id)}>
                <ListItemIcon>
                  <MarkEmailReadIcon
                    sx={{ color: '#5ea8eb' }}
                    fontSize="small"
                  />
                </ListItemIcon>
                Marcar como lida
              </MenuItem>
            </Menu>
          </BodyCellStyled>
        )}
      </TableRowStyled>
    );
  };

  const getFeedbacks = async () => {
    try {
      const {
        data: { data },
      } = await api.get('/feedbacks', { headers: authHeader() });
      setFeedbacks(
        data.sort((a, b) => b.updated_at.localeCompare(a.updated_at))
      );
      setFilteredFeedbacks(
        data.sort((a, b) => b.updated_at.localeCompare(a.updated_at))
      );
      setLoading(false);
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    getFeedbacks();
  }, []);

  useEffect(() => {
    if (feedbacks.length === 0) return;

    let newFilteredFeedbacks = [...feedbacks];

    if (searchValue) {
      newFilteredFeedbacks = newFilteredFeedbacks.filter(
        ({ subject, description }) =>
          subject.toLowerCase().includes(searchValue.toLowerCase()) ||
          description.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    if (filterOptions?.read) {
      const isRead = filterOptions.read === 'read';
      newFilteredFeedbacks = newFilteredFeedbacks.filter(
        (feedback) => feedback.read_by_admin === isRead
      );
    }

    if (filterOptions?.date) {
      const daysFilter = Number(filterOptions.date);
      if (!Number.isNaN(daysFilter)) {
        const currentDate = new Date();
        const filterDate = new Date();
        filterDate.setDate(currentDate.getDate() - daysFilter);

        newFilteredFeedbacks = newFilteredFeedbacks.filter((feedback) => {
          const feedbackDate = new Date(feedback.updated_at);
          return feedbackDate >= filterDate;
        });
      }
    }

    if (filterOptions?.order) {
      if (filterOptions.order === 'earlier') {
        newFilteredFeedbacks.sort((a, b) =>
          a.updated_at.localeCompare(b.updated_at)
        );
      } else if (filterOptions.order === 'recent') {
        newFilteredFeedbacks.sort((a, b) =>
          b.updated_at.localeCompare(a.updated_at)
        );
      }
    } else {
      newFilteredFeedbacks.sort((a, b) =>
        b.updated_at.localeCompare(a.updated_at)
      );
    }

    setFilteredFeedbacks(newFilteredFeedbacks);
  }, [searchValue, filterOptions, feedbacks]);

  if (loading) {
    return (
      <RootContainer>
        <LinearProgress style={{ width: '80%' }} />
      </RootContainer>
    );
  }

  return (
    <RootContainer>
      {toaster.open && <ToasterBox message={toaster.msg} showSeconds={5} />}
      {feedbacks.length === 0 ? (
        <NoTicketsMsg>
          Você ainda não possui nenhum ticket em andamento
        </NoTicketsMsg>
      ) : (
        <Container>
          <Table>
            <TableHead sx={{ minWidth: '800px' }}>
              <HeadRowStyled>
                <HeadCellStyled sx={{ width: '10%' }}>Status</HeadCellStyled>
                <HeadCellStyled sx={{ width: '4%' }}>ID</HeadCellStyled>
                <HeadCellStyled sx={{ width: '15%' }}>
                  Tipo de feedback
                </HeadCellStyled>
                <HeadCellStyled sx={{ width: '10%' }}>
                  Prioridade
                </HeadCellStyled>
                <HeadCellStyled sx={{ width: '20%' }}>Assunto</HeadCellStyled>
                <HeadCellStyled sx={{ width: '35%' }}>Mensagem</HeadCellStyled>
                {[1, 164].includes(userId) && (
                  <HeadCellStyled sx={{ width: '4%' }} />
                )}
              </HeadRowStyled>
            </TableHead>
            {filteredFeedbacks.length > 0 && (
              <TableBody>
                {filteredFeedbacks.map((item) => renderTableRows(item))}
              </TableBody>
            )}
          </Table>
        </Container>
      )}
    </RootContainer>
  );
}
